import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Field, Input, makeStyles, tokens } from "@fluentui/react-components";
import { useResetPassword, useRequestPasswordReset } from "../hooks/useIdentityMutations";
import { useNotify } from "../../../shared/components/SuperToast";

import appIcon from "../../../assets/logo.svg";
import { t } from "i18next";

const turnstileSiteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();
const turnstileIframeBaseUrl = (process.env.REACT_APP_TURNSTILE_IFRAME_URL || "").trim();

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorNeutralBackground3,
    minHeight: "100vh",
    width: "100%",
  },
  form: {
    width: "400px",
    padding: "32px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: "8px",
    boxShadow: tokens.shadow16,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
  },
  logo: {
    width: "64px",
    height: "64px",
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    margin: 0,
    textAlign: "center",
  },
  field: {
    marginBottom: "16px",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "24px",
  },
  link: {
    textAlign: "center",
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  successMessage: {
    padding: "16px",
    backgroundColor: tokens.colorPaletteGreenBackground2,
    borderRadius: "4px",
    marginBottom: "16px",
    textAlign: "center",
    color: tokens.colorPaletteGreenForeground2,
  },
});

export default function PasswordReset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const styles = useStyles();
  const { notify } = useNotify();

  const token = searchParams.get("token");
  const slug = window.location.hostname.split(".")[0];
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [isTurnstileReady, setIsTurnstileReady] = useState(!turnstileSiteKey);
  const [turnstileError, setTurnstileError] = useState("");

  const turnstileIframeUrl = (() => {
    if (!turnstileSiteKey) return "";
    try {
      const url = new URL(turnstileIframeBaseUrl, window.location.origin);
      url.searchParams.set("sitekey", turnstileSiteKey);
      url.searchParams.set("parentOrigin", window.location.origin);
      return url.toString();
    } catch {
      return "";
    }
  })();

  useEffect(() => {
    if (!turnstileSiteKey) return;
    if (!turnstileIframeUrl) {
      setIsTurnstileReady(false);
      setTurnstileError(t("nounPhrases.captchaIframeConfig", "CAPTCHA iframe URL is invalid."));
      return;
    }

    const expectedOrigin = new URL(turnstileIframeUrl).origin;

    setIsTurnstileReady(false);
    setTurnstileToken("");
    setTurnstileError("");

    const onMessage = (event) => {
      if (event.origin !== expectedOrigin) return;

      const payload = event.data;
      if (!payload || payload.source !== "itsm-turnstile") return;

      if (payload.type === "ready") {
        setIsTurnstileReady(true);
        setTurnstileError("");
        return;
      }

      if (payload.type === "token") {
        setIsTurnstileReady(true);
        setTurnstileToken(payload.token || "");
        setTurnstileError("");
        return;
      }

      if (payload.type === "expired") {
        setTurnstileToken("");
        return;
      }

      if (payload.type === "error") {
        setTurnstileToken("");
        setIsTurnstileReady(false);
        setTurnstileError(
          t(
            "nounPhrases.captchaLoadError",
            "CAPTCHA failed to load. Check ad-blockers, DNS filtering, or site key domain settings.",
          ) + (payload.errorCode ? ` (code: ${payload.errorCode})` : ""),
        );
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [turnstileIframeUrl]);

  const handleResetSuccess = (data) => {
    //notify(data.message || "Password reset successfully!", "success");
    setSuccess(true);
    setTimeout(() => {
      navigate("/login");
    }, 3000);
  };

  const handleRequestSuccess = (data) => {
    //notify(data.message || "Password reset email sent!", "success");
    setSuccess(true);
  };

  const handleError = (error) => {
    notify(error.message || "Failed to process request", "error");
  };

  const resetPasswordMutation = useResetPassword(handleResetSuccess, handleError, true);
  const requestResetMutation = useRequestPasswordReset(handleRequestSuccess, handleError, true);

  const validateForm = () => {
    const errors = {};

    if (token) {
      // Validating password reset form
      if (!newPassword) {
        errors.newPassword = "Password is required";
      } else if (newPassword.length < 8) {
        errors.newPassword = "Password must be at least 8 characters";
      }

      if (!confirmPassword) {
        errors.confirmPassword = "Please confirm your password";
      } else if (newPassword !== confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
    } else {
      // Validating email request form
      if (!email) {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      notify(t("nounPhrases.completeCaptcha", "Please complete the CAPTCHA."), "error");
      return;
    }

    if (token) {
      // Reset password with token
      resetPasswordMutation.mutate({ tokenHash: token, newPassword, turnstileToken });
    } else {
      // Request password reset email
      requestResetMutation.mutate({ email, slug, turnstileToken });
    }
  };

  // Request password reset form (no token)
  if (!token) {
    return (
      <div className={styles.container}>
        <div className={styles.form}>
          <div className={styles.header}>
            <img src={appIcon} alt="Logo" className={styles.logo} />
            <h1 className={styles.title}>{t("nounPhrases.resetPassword")}</h1>
            {!success && <p className={styles.subtitle}>{t("Phrases.enterEmailToGetResetLink")}</p>}
          </div>

          {success && <div className={styles.successMessage}>{t("Phrases.confirmationResetPasswordLinkSent")}</div>}

          {!success && (
            <form onSubmit={handleSubmit}>
              <Field
                className={styles.field}
                label={t("generic.email")}
                required
                validationMessage={validationErrors.email}
                validationState={validationErrors.email ? "error" : undefined}
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("nounPhrases.enterEmail")}
                  disabled={requestResetMutation.isPending}
                />
              </Field>

              {turnstileSiteKey && (
                <>
                  <div style={{ width: "100%", marginBottom: "8px" }}>
                    <iframe
                      title="Cloudflare Turnstile"
                      src={turnstileIframeUrl}
                      style={{ width: "100%", height: "72px", border: "none" }}
                      scrolling="no"
                    />
                  </div>
                  {!isTurnstileReady && (
                    <div style={{ color: tokens.colorNeutralForeground3, fontSize: "12px", marginBottom: "10px" }}>
                      {t("nounPhrases.loadingCaptcha", "Loading CAPTCHA...")}
                    </div>
                  )}
                  {!!turnstileError && (
                    <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: "12px", marginBottom: "10px" }}>
                      {turnstileError}
                    </div>
                  )}
                  {isTurnstileReady && !turnstileToken && !turnstileError && (
                    <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: "12px", marginBottom: "10px" }}>
                      {t("nounPhrases.completeCaptcha", "Please complete the CAPTCHA.")}
                    </div>
                  )}
                </>
              )}

              <div className={styles.footer}>
                <Button
                  appearance="primary"
                  type="submit"
                  disabled={
                    !email ||
                    requestResetMutation.isPending ||
                    (turnstileSiteKey && (!isTurnstileReady || !turnstileToken))
                  }
                >
                  {requestResetMutation.isPending ? t("generic.sending") + "..." : t("nounPhrases.sendResetLink")}
                </Button>

                <div className={styles.link} onClick={() => navigate("/login")}>
                  {t("nounPhrases.backToLogin", "Back to Login")}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Reset password form (with token)
  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <div className={styles.header}>
          <img src={appIcon} alt="Logo" className={styles.logo} />
          <h1 className={styles.title}>Reset Password</h1>
          <p className={styles.subtitle}>Enter your new password below</p>
        </div>

        {success && <div className={styles.successMessage}>Password reset successfully! Redirecting to login...</div>}

        {!success && (
          <form onSubmit={handleSubmit}>
            <Field
              className={styles.field}
              label="New Password"
              required
              validationMessage={validationErrors.newPassword}
              validationState={validationErrors.newPassword ? "error" : undefined}
            >
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={resetPasswordMutation.isPending}
              />
            </Field>

            <Field
              className={styles.field}
              label="Confirm Password"
              required
              validationMessage={validationErrors.confirmPassword}
              validationState={validationErrors.confirmPassword ? "error" : undefined}
            >
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={resetPasswordMutation.isPending}
              />
            </Field>

            {turnstileSiteKey && (
              <>
                <div style={{ width: "100%", marginBottom: "8px" }}>
                  <iframe
                    title="Cloudflare Turnstile"
                    src={turnstileIframeUrl}
                    style={{ width: "100%", height: "72px", border: "none" }}
                    scrolling="no"
                  />
                </div>
                {!isTurnstileReady && (
                  <div style={{ color: tokens.colorNeutralForeground3, fontSize: "12px", marginBottom: "10px" }}>
                    {t("nounPhrases.loadingCaptcha", "Loading CAPTCHA...")}
                  </div>
                )}
                {!!turnstileError && (
                  <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: "12px", marginBottom: "10px" }}>
                    {turnstileError}
                  </div>
                )}
                {isTurnstileReady && !turnstileToken && !turnstileError && (
                  <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: "12px", marginBottom: "10px" }}>
                    {t("nounPhrases.completeCaptcha", "Please complete the CAPTCHA.")}
                  </div>
                )}
              </>
            )}

            <div className={styles.footer}>
              <Button
                appearance="primary"
                type="submit"
                disabled={
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword ||
                  resetPasswordMutation.isPending ||
                  (turnstileSiteKey && (!isTurnstileReady || !turnstileToken))
                }
              >
                {resetPasswordMutation.isPending ? "Resetting..." : "Reset Password"}
              </Button>

              <div className={styles.link} onClick={() => navigate("/login")}>
                Back to Login
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
